import { Button } from '@/components/ui/button';
import { Card, CardFooter, CardHeader } from '@/components/ui/card';
import { Artifact } from '@/types/artifact';
import { formatDate } from '@/utils/formatters';
import { Link } from '@tanstack/react-router';
import React, { useCallback } from 'react';

interface ArtifactCardProps {
  artifact: Artifact;
  onDelete?: (artifactId: string) => void;
  className?: string;
}

export const ArtifactCard: React.FC<ArtifactCardProps> = React.memo(({
  artifact,
  onDelete,
  className,
}) => {
  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete?.(artifact.id);
  }, [artifact.id, onDelete]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };


  return (
    <Link
      to="/editor/$id"
      params={{ id: artifact.id }}
      className={`block group ${className || ''}`}
    >
      <Card className="h-full transition-all hover:shadow-md hover:-translate-y-1 cursor-pointer overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                <HtmlIcon className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                {artifact.title}
              </h3>
              
              <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <SizeIcon className="h-3 w-3" />
                    <span>{formatFileSize(artifact.metadata.size)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <ClockIcon className="h-3 w-3" />
                    <span>Updated: {formatDate(new Date(artifact.updatedAt))}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <CalendarIcon className="h-3 w-3" />
                  <span>Created: {formatDate(new Date(artifact.createdAt))}</span>
                </div>
              </div>
            </div>

            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive h-8 w-8 p-0 flex-shrink-0"
                onClick={handleDeleteClick}
              >
                <DeleteIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>


        {artifact.metadata.hasExternalResources && (
          <CardFooter className="pt-0 pb-3">
            <div className="flex items-center gap-1 text-warning text-xs">
              <ExternalIcon className="h-3 w-3" />
              <span>Contains external resources</span>
            </div>
          </CardFooter>
        )}
      </Card>
    </Link>
  );
});

// Icons
const HtmlIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3 3 3-3v6l-3-3-3 3V2zM4.5 9L6 7.5 7.5 9 6 10.5 4.5 9zm15 0L21 7.5 22.5 9 21 10.5 19.5 9zM12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 9l-3-3-3 3v-6l3 3 3-3v6z" />
  </svg>
);


const DeleteIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m3 6 3 0 m0 0 16 0 M7 6v14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V6M10 11v6 M14 11v6 M14 6V4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v2" />
  </svg>
);

const SizeIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14,2 14,8 20,8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10,9 9,9 8,9" />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12,6 12,12 16,14" />
  </svg>
);

const ExternalIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15,3 21,3 21,9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const CalendarIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
); 